import moment from 'moment';
import { TIMELINE_WORKING_HOURS } from '../../config/timelineWorkingHours';

export const GANTT_LABEL_WIDTH_PX = 220;
export const GANTT_DATETIME_FORMAT = 'DD MMM HH:mm';
export const GANTT_DATE_FORMAT = 'DD MMM YYYY';
export const GANTT_TIME_FORMAT = 'HH:mm';
export const MIN_TASK_DURATION_HOURS = 0.25;
/** Library drag grid (matches Gantt `timeStep`); used when resizing edges. */
export const RESIZE_SNAP_MINUTES = 15;

export const EXCLUDED_TIMELINE_USER_TYPES = ['Ex_employee', 'Client'];

export function isActiveTeamMember(user) {
    if (!user?.user_type) return true;
    return !EXCLUDED_TIMELINE_USER_TYPES.includes(user.user_type);
}

const MAX_AXIS_TICKS = 7;
const MIN_TICK_GAP_PCT = 11;

export function formatGanttDateTime(value) {
    if (!value) return '—';
    const m = moment(value);
    return m.isValid() ? m.format(GANTT_DATETIME_FORMAT) : '—';
}

export function formatGanttRangeLabel(rangeStart, rangeEnd) {
    const start = moment(rangeStart);
    const end = moment(rangeEnd);
    if (!start.isValid() || !end.isValid()) return '—';

    if (start.isSame(end, 'day')) {
        return `${start.format('DD MMM YYYY')}, ${start.format(GANTT_TIME_FORMAT)} – ${end.format(GANTT_TIME_FORMAT)}`;
    }

    return `${formatGanttDateTime(start)} – ${formatGanttDateTime(end)}`;
}

/** Screenshot-style range: "Mon 7 Sep 00:00 to Mon 14 Sep 00:00" */
export function formatTimelineScopeRange(rangeStart, rangeEnd) {
    const start = moment(rangeStart);
    const end = moment(rangeEnd);
    if (!start.isValid() || !end.isValid()) return '—';
    return `${start.format('ddd D MMM HH:mm')} to ${end.format('ddd D MMM HH:mm')}`;
}

/**
 * Clamp a moment into the timeline working window (default 10:00–21:00).
 * Times before startHour snap to startHour same day; times at/after endHour
 * snap to startHour the next day.
 */
export function clampDateToWorkingHours(value, { allowOutsideWindow = false } = {}) {
    const m = moment(value);
    if (!m.isValid() || allowOutsideWindow) return m;
    const { startHour, endHour } = TIMELINE_WORKING_HOURS;
    const hourFrac = m.hours() + m.minutes() / 60 + m.seconds() / 3600;
    if (hourFrac < startHour) {
        return m.clone().hours(startHour).minutes(0).seconds(0).milliseconds(0);
    }
    if (hourFrac >= endHour) {
        return m.clone().add(1, 'day').hours(startHour).minutes(0).seconds(0).milliseconds(0);
    }
    return m;
}

/** Hours inside the timeline working window between two moments. */
export function workingHoursBetweenWindow(startDate, endDate) {
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return 0;
    const { startHour, endHour } = TIMELINE_WORKING_HOURS;
    let total = 0;
    const day = start.clone().startOf('day');
    const last = end.clone().startOf('day');
    while (day.isSameOrBefore(last)) {
        const dayStart = day.clone().hour(startHour).minute(0).second(0).millisecond(0);
        const dayEnd = day.clone().hour(endHour).minute(0).second(0).millisecond(0);
        const segStart = moment.max(start, dayStart);
        const segEnd = moment.min(end, dayEnd);
        if (segEnd.isAfter(segStart)) {
            total += segEnd.diff(segStart, 'hours', true);
        }
        day.add(1, 'day');
    }
    return Math.round(total * 10) / 10;
}

/**
 * Collect moments for the gantt range.
 * Uses actual scheduled work windows as primary boundaries.
 * Deadlines are included only if they fall within 3 days of the last work end,
 * so a far-future due date on one task doesn't squash all bars.
 */
export function collectGanttTaskMoments(tasks = [], options = {}) {
    const previewMap = options.previewMap || null;
    const workMoments = [];
    const deadlineMoments = [];

    for (const task of tasks) {
        if (task.scheduled_start_at) workMoments.push(moment(task.scheduled_start_at));
        else if (task.planned_start_at) workMoments.push(moment(task.planned_start_at));

        const proposed = previewMap?.get?.(task.id);
        if (proposed) workMoments.push(moment(proposed));
        if (task.timeline_end_at) workMoments.push(moment(task.timeline_end_at));
        if (task.scheduled_end_at) workMoments.push(moment(task.scheduled_end_at));
        if (task.deadline_at) deadlineMoments.push(moment(task.deadline_at));
    }

    const validWork = workMoments.filter((m) => m.isValid());
    if (!validWork.length) {
        // No work windows at all — fall back to including all deadlines
        return [...validWork, ...deadlineMoments].filter((m) => m.isValid());
    }

    // Only pull in deadlines within 3 days of the last scheduled work end
    const workMax = moment.max(validWork);
    const cutoff = workMax.clone().add(3, 'days');
    const nearDeadlines = deadlineMoments.filter(
        (m) => m.isValid() && m.isSameOrBefore(cutoff)
    );

    return [...validWork, ...nearDeadlines];
}

export function buildGanttRangeFromMoments(moments = []) {
    const valid = moments.filter((m) => m && moment(m).isValid()).map((m) => moment(m));
    if (!valid.length) {
        const start = moment().startOf('day');
        return {
            rangeStart: start,
            rangeEnd: start.clone().add(1, 'day'),
        };
    }

    let min = moment.min(valid);
    let max = moment.max(valid);
    const spanHours = Math.max(max.diff(min, 'hours', true), 0.5);
    const padHours = spanHours <= 12 ? 1 : spanHours <= 36 ? 2 : spanHours <= 96 ? 6 : 12;

    min = min.clone().subtract(padHours, 'hours');
    max = max.clone().add(padHours, 'hours');

    if (max.diff(min, 'hours', true) < 6) {
        max = min.clone().add(6, 'hours');
    }

    return { rangeStart: min, rangeEnd: max };
}

/**
 * Zoom-out reveals calendar context rather than only shrinking the same hours.
 * The base range remains unchanged at 100% and above.
 */
export function expandGanttRangeForZoom(rangeStart, rangeEnd, zoom = 1) {
    const start = moment(rangeStart);
    const end = moment(rangeEnd);
    if (!start.isValid() || !end.isValid()) {
        return { rangeStart: start, rangeEnd: end };
    }

    const z = Number(zoom) || 1;
    let paddingDays = 0;
    if (z <= 0.25) paddingDays = 7;
    else if (z <= 0.35) paddingDays = 4;
    else if (z <= 0.5) paddingDays = 2;
    else if (z <= 0.7) paddingDays = 1;

    if (!paddingDays) {
        return { rangeStart: start, rangeEnd: end };
    }

    return {
        rangeStart: start.clone().startOf('day').subtract(paddingDays, 'days'),
        rangeEnd: end.clone().endOf('day').add(paddingDays, 'days'),
    };
}

function pickTickStep(rangeStart, rangeEnd, maxTicks = MAX_AXIS_TICKS) {
    const rangeHours = rangeEnd.diff(rangeStart, 'hours', true);
    const candidates = [
        { unit: 'hours', amount: 1 },
        { unit: 'hours', amount: 2 },
        { unit: 'hours', amount: 3 },
        { unit: 'hours', amount: 4 },
        { unit: 'hours', amount: 6 },
        { unit: 'hours', amount: 8 },
        { unit: 'hours', amount: 12 },
        { unit: 'days', amount: 1 },
        { unit: 'days', amount: 2 },
        { unit: 'days', amount: 3 },
        { unit: 'days', amount: 7 },
        { unit: 'days', amount: 14 },
    ];

    for (const step of candidates) {
        const count = rangeEnd.diff(rangeStart, step.unit) / step.amount;
        if (count <= maxTicks) {
            return step;
        }
    }

    return { unit: 'days', amount: Math.ceil(rangeEnd.diff(rangeStart, 'days') / maxTicks) || 1 };
}

function alignCursorToStep(cursor, step) {
    if (step.unit === 'hours') {
        cursor.startOf('hour');
        const hour = cursor.hour();
        cursor.hour(hour - (hour % step.amount)).minute(0).second(0);
        return;
    }
    cursor.startOf('day');
}

function formatTickLabel(cursor, previous, step, timeFormat = GANTT_TIME_FORMAT) {
    const isNewDay = !previous || !cursor.isSame(previous, 'day');

    if (step.unit === 'days') {
        return {
            label: cursor.format('DD MMM'),
            sublabel: cursor.format(timeFormat),
            isMajor: true,
        };
    }

    if (isNewDay) {
        return {
            label: cursor.format('DD MMM'),
            sublabel: cursor.format(timeFormat),
            isMajor: true,
        };
    }

    return {
        label: cursor.format(timeFormat),
        sublabel: null,
        isMajor: false,
    };
}

function filterTicksBySpacing(ticks, timelineWidthPx = 0, options = {}) {
    if (ticks.length <= 2) return ticks;

    const labelMinPx = Number(options.labelMinPx) || 64;
    const minGapPctFloor =
        options.minGapPctFloor != null ? Number(options.minGapPctFloor) : MIN_TICK_GAP_PCT;

    const minGapPct =
        timelineWidthPx > 0
            ? Math.max(minGapPctFloor, (labelMinPx / timelineWidthPx) * 100)
            : MIN_TICK_GAP_PCT;

    const filtered = [ticks[0]];
    for (let i = 1; i < ticks.length; i += 1) {
        const tick = ticks[i];
        const prev = filtered[filtered.length - 1];
        if (tick.leftPct - prev.leftPct >= minGapPct) {
            filtered.push(tick);
            continue;
        }
        if (tick.isMajor && !prev.isMajor) {
            filtered[filtered.length - 1] = tick;
        }
    }

    const last = ticks[ticks.length - 1];
    const tail = filtered[filtered.length - 1];
    if (tail.key !== last.key && (last.leftPct - tail.leftPct >= minGapPct * 0.75 || last.isMajor)) {
        filtered.push(last);
    }

    return filtered;
}

export function buildDateTicks(rangeStart, rangeEnd, options = {}) {
    const timelineWidthPx = Number(options.timelineWidthPx) || 0;
    // Allow many raw ticks — filterTicksBySpacing will prune them to a readable density
    const maxTicks =
        timelineWidthPx > 0
            ? Math.max(MAX_AXIS_TICKS, Math.floor(timelineWidthPx / 60))
            : MAX_AXIS_TICKS;
    const totalMs = Math.max(rangeEnd.diff(rangeStart), 1);
    const step = options.stepOverride || pickTickStep(rangeStart, rangeEnd, maxTicks);
    const timeFormat = options.timeFormat || GANTT_TIME_FORMAT;
    const cursor = rangeStart.clone();
    alignCursorToStep(cursor, step);

    const rawTicks = [];
    let previous = null;
    const end = rangeEnd.clone();

    while (cursor.isSameOrBefore(end)) {
        const { label, sublabel, isMajor } = formatTickLabel(cursor, previous, step, timeFormat);
        rawTicks.push({
            date: cursor.clone(),
            leftPct: Math.max(0, Math.min(100, (cursor.diff(rangeStart) / totalMs) * 100)),
            label,
            sublabel,
            isMajor,
            key: cursor.toISOString(),
        });
        previous = cursor.clone();
        cursor.add(step.amount, step.unit);
    }

    return filterTicksBySpacing(rawTicks, timelineWidthPx, {
        labelMinPx: options.labelMinPx,
        minGapPctFloor: options.minGapPctFloor,
    });
}

export function formatTaskContext(task) {
    const board = (task?.board_name || '').trim();
    const project = (task?.project?.project_name || '').trim();

    if (!board && !project) return null;
    if (!board) return project;
    if (!project) return board;

    const boardLower = board.toLowerCase();
    const projectLower = project.toLowerCase();
    if (boardLower === projectLower) return board;
    if (boardLower.includes(projectLower) || projectLower.includes(boardLower)) {
        return board.length >= project.length ? board : project;
    }

    return `${board} · ${project}`;
}

export function formatTaskScheduleSummary(task) {
    const parts = [];
    const start = task?.scheduled_start_at ? moment(task.scheduled_start_at) : null;
    const workEnd = task?.scheduled_end_at ? moment(task.scheduled_end_at) : null;
    const due = task?.deadline_at ? moment(task.deadline_at) : null;

    // Show actual work window (start → scheduled_end), not start → deadline
    if (start?.isValid() && workEnd?.isValid()) {
        if (start.isSame(workEnd, 'day')) {
            parts.push(`${start.format('DD MMM')}, ${start.format(GANTT_TIME_FORMAT)}–${workEnd.format(GANTT_TIME_FORMAT)}`);
        } else {
            parts.push(`${formatGanttDateTime(start)} → ${formatGanttDateTime(workEnd)}`);
        }
        // Append deadline separately so it's clear
        if (due?.isValid()) {
            if (due.isBefore(workEnd)) {
                parts.push(`⚠ due ${due.format('DD MMM HH:mm')}`);
            } else if (!due.isSame(workEnd, 'minute')) {
                parts.push(`due ${due.format('DD MMM HH:mm')}`);
            }
        }
    } else if (start?.isValid()) {
        parts.push(`Starts ${formatGanttDateTime(start)}`);
        if (due?.isValid()) parts.push(`due ${due.format('DD MMM HH:mm')}`);
    } else if (due?.isValid()) {
        parts.push(`Due ${formatGanttDateTime(due)}`);
    }

    const hours = task?.allocated_hours ?? task?.estimated_hours;
    if (hours != null) {
        parts.push(`${hours}h`);
    }

    return parts.join(' · ');
}

export function formatAssignees(assignees = []) {
    if (!assignees.length) return null;
    return assignees
        .map((a) => {
            const name = `${a.first_name || ''} ${a.last_name || ''}`.trim() || `User ${a.user_id}`;
            return a.allocated_hours != null ? `${name} (${a.allocated_hours}h)` : name;
        })
        .join(', ');
}

export function formatDependencyList(deps = []) {
    if (!deps.length) return null;
    return deps.map((d) => d.title || `#${d.card_id}`).join(', ');
}

/** Distinct bar colors per task (stable by card id — not by priority). */
export const GANTT_BAR_PALETTE = [
    { background: '#37b24d', borderColor: '#2f9e44' }, // green
    { background: '#339af0', borderColor: '#1c7ed6' }, // blue
    { background: '#e64980', borderColor: '#c2255c' }, // magenta
    { background: '#845ef7', borderColor: '#7048e8' }, // violet
    { background: '#15aabf', borderColor: '#0c8599' }, // cyan
    { background: '#f59f00', borderColor: '#e67700' }, // amber
    { background: '#f06595', borderColor: '#d6336c' }, // pink
    { background: '#20c997', borderColor: '#12b886' }, // teal
];

export function ganttBarAccent(task = {}) {
    // Status overrides only — otherwise each card gets a different palette color.
    if (task.missed_deadline) {
        return { background: '#fa5252', borderColor: '#e03131' };
    }
    const id = Math.abs(Number(task.id) || 0);
    // Mix in assignee so multi-person copies of the same card can differ slightly.
    const seed = id + (Number(task.gantt_user_id) || 0) * 7;
    return GANTT_BAR_PALETTE[seed % GANTT_BAR_PALETTE.length];
}

/** Vertical waterfall layout so adjacent bars don’t stick on one line. */
export const GANTT_BAR_LANE = {
    topPad: 6,
    barHeight: 28,
    gap: 12,
    get stride() {
        return this.barHeight + this.gap;
    },
};

export function ganttBarLaneTopPx(lane = 0) {
    const i = Math.max(0, Number(lane) || 0);
    return GANTT_BAR_LANE.topPad + i * GANTT_BAR_LANE.stride;
}

export function ganttBarLaneStyle(lane = 0) {
    const top = ganttBarLaneTopPx(lane);
    return {
        // CSS vars beat `.team-bar { top: 6px }` / track height:28px collisions.
        '--gantt-lane-top': `${top}px`,
        '--gantt-lane-height': `${GANTT_BAR_LANE.barHeight}px`,
        top: `${top}px`,
        height: `${GANTT_BAR_LANE.barHeight}px`,
    };
}

export function ganttTrackHeightPx(taskCount = 1) {
    const n = Math.max(1, Number(taskCount) || 1);
    return GANTT_BAR_LANE.topPad + n * GANTT_BAR_LANE.stride + 4;
}

const GANTT_PRIORITY_RANK = {
    urgent: 0,
    high: 1,
    medium: 2,
    low: 3,
};

export function ganttPriorityRank(priority) {
    const v = String(priority || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(GANTT_PRIORITY_RANK, v)
        ? GANTT_PRIORITY_RANK[v]
        : 2;
}

export function ganttTaskStartMoment(task) {
    const raw =
        task?.scheduled_start_at ||
        task?.planned_start_at ||
        task?.preferred_start_at ||
        task?.window_start_at ||
        task?.created_date;
    const m = moment(raw);
    return m.isValid() ? m : null;
}

export function ganttTaskEndMoment(task) {
    const start = ganttTaskStartMoment(task);
    const raw = task?.scheduled_end_at || task?.timeline_end_at;
    const end = raw ? moment(raw) : null;
    if (end?.isValid() && (!start || end.isAfter(start))) return end;
    const hours = Math.max(
        MIN_TASK_DURATION_HOURS,
        Number(task?.allocated_hours ?? task?.estimated_hours) || 1
    );
    return start ? start.clone().add(hours, 'hours') : null;
}

function compareGanttTimelineTasks(a, b) {
    const as = ganttTaskStartMoment(a)?.valueOf() ?? Number.MAX_SAFE_INTEGER;
    const bs = ganttTaskStartMoment(b)?.valueOf() ?? Number.MAX_SAFE_INTEGER;
    if (as !== bs) return as - bs;
    const pr = ganttPriorityRank(a?.priority) - ganttPriorityRank(b?.priority);
    if (pr !== 0) return pr;
    return Number(a?.id) - Number(b?.id);
}

/**
 * Group tasks whose work windows overlap. Groups stay in time order;
 * within a group, higher priority is first (shown above).
 */
export function clusterOverlappingGanttTasks(tasks = []) {
    const sorted = [...(tasks || [])].sort(compareGanttTimelineTasks);
    const groups = [];
    sorted.forEach((task) => {
        const start = ganttTaskStartMoment(task)?.valueOf() ?? Number.MAX_SAFE_INTEGER;
        const end = ganttTaskEndMoment(task)?.valueOf() ?? start;
        const last = groups[groups.length - 1];
        if (last && start < last.maxEnd - 1000) {
            last.tasks.push(task);
            last.maxEnd = Math.max(last.maxEnd, end);
            return;
        }
        groups.push({ tasks: [task], maxEnd: end });
    });
    groups.forEach((group) => {
        group.tasks.sort((a, b) => {
            const pr = ganttPriorityRank(a?.priority) - ganttPriorityRank(b?.priority);
            if (pr !== 0) return pr;
            return compareGanttTimelineTasks(a, b);
        });
    });
    return groups;
}

/**
 * Vertical order on the chart: earlier scheduled work above later work.
 * Overlapping (merging) tasks stack by priority, then start time.
 */
export function sortGanttRowTasks(tasks = []) {
    return clusterOverlappingGanttTasks(tasks).flatMap((group) => group.tasks);
}

/** Keep queue index on tasks (0 = first in queue). Does not reorder by time. */
export function stampGanttQueueOrder(tasks = []) {
    return (tasks || []).map((t, i) => ({
        ...t,
        sort_order: t?.sort_order != null ? Number(t.sort_order) : i,
    }));
}

/** After a manual queue reorder, rewrite sort_order from the new list index. */
export function restampGanttQueueOrder(tasks = []) {
    return (tasks || []).map((t, i) => ({ ...t, sort_order: i }));
}

/** Set window.__GANTT_LANE_DEBUG__ = false to silence. */
export const GANTT_LANE_DEBUG =
    typeof window === 'undefined' || window.__GANTT_LANE_DEBUG__ !== false;

export function ganttLaneLog(stage, payload = {}) {
    if (!GANTT_LANE_DEBUG) return;
    // eslint-disable-next-line no-console
    console.log(`[gantt-lane] ${stage}`, {
        t: new Date().toISOString(),
        ...payload,
    });
}

/** Convert a client X position on a track into a moment on the timeline. */
export function timeFromTrackClientX(trackEl, clientX, rangeStart, totalMs) {
    if (!trackEl || !totalMs) return moment(rangeStart);
    const rect = trackEl.getBoundingClientRect();
    // Use layout width only — mixing scrollWidth with the visible rect maps
    // the pointer onto the start of the range while the chart is scrolled.
    const width = Math.max(trackEl.offsetWidth || 0, 1);
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / width));
    return moment(rangeStart).add(frac * totalMs, 'milliseconds');
}

/** Continuous drop position on a track (no hour snap) — matches Team Planner drag-over. */
export function dropLeftPctFromTrackEvent(trackEl, clientX) {
    if (!trackEl) return 0;
    const rect = trackEl.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / (rect.width || 1)));
    return frac * 100;
}

/**
 * While HTML5-dragging a bar near the viewport edges, auto-scroll the timeline
 * so the drop target stays on-screen instead of the ghost leaving the chart.
 * Uses velocity + easing (not big per-frame jumps) for smoother motion.
 */
const GANTT_AUTO_SCROLL = {
    edgePx: 96,
    minSpeed: 280, // px/sec at edge start
    maxSpeed: 1400, // px/sec at hard edge
    ease: 0.18, // velocity lerp factor per frame (~smooth ramp)
    rafId: null,
    targetSpeed: 0,
    speed: 0,
    carry: 0,
    lastTs: 0,
    viewport: null,
    listening: false,
};

function findGanttScrollViewport(fromEl) {
    if (fromEl?.closest) {
        const near = fromEl.closest('.user-gantt-scroll-viewport');
        if (near) return near;
    }
    return document.querySelector('.user-gantt-scroll-viewport');
}

function ensureGanttAutoScrollLoop() {
    if (GANTT_AUTO_SCROLL.rafId != null) return;
    GANTT_AUTO_SCROLL.lastTs = 0;
    GANTT_AUTO_SCROLL.rafId = requestAnimationFrame(ganttAutoScrollTick);
}

function ganttAutoScrollTick(ts) {
    const state = GANTT_AUTO_SCROLL;
    const el = state.viewport;
    if (!el) {
        state.rafId = null;
        state.speed = 0;
        state.targetSpeed = 0;
        state.carry = 0;
        return;
    }

    const last = state.lastTs || ts;
    const dt = Math.min(0.05, Math.max(0.001, (ts - last) / 1000));
    state.lastTs = ts;

    // Ease current speed toward target for less jerky start/stop.
    state.speed += (state.targetSpeed - state.speed) * state.ease;
    if (Math.abs(state.speed) < 0.5 && Math.abs(state.targetSpeed) < 0.5) {
        state.speed = 0;
        state.targetSpeed = 0;
        state.carry = 0;
        state.rafId = null;
        return;
    }

    state.carry += state.speed * dt;
    const pixels = state.carry > 0 ? Math.floor(state.carry) : Math.ceil(state.carry);
    if (pixels !== 0) {
        state.carry -= pixels;
        const max = Math.max(0, el.scrollWidth - el.clientWidth);
        const prev = el.scrollLeft;
        const next = Math.max(0, Math.min(max, prev + pixels));
        if (next !== prev) {
            el.scrollLeft = next;
        } else {
            // Hit scroll bound — damp velocity so we don't keep fighting the edge.
            state.speed = 0;
            state.targetSpeed = 0;
            state.carry = 0;
        }
    }

    state.rafId = requestAnimationFrame(ganttAutoScrollTick);
}

export function updateGanttDragAutoScroll(clientX, fromEl = null) {
    const viewport = findGanttScrollViewport(fromEl);
    if (!viewport || clientX == null) {
        GANTT_AUTO_SCROLL.targetSpeed = 0;
        return;
    }
    GANTT_AUTO_SCROLL.viewport = viewport;
    const rect = viewport.getBoundingClientRect();
    // Timeline-only panes set --gantt-label-width: 0; classic sticky layout keeps the pad.
    const labelVar = Number.parseFloat(
        getComputedStyle(viewport).getPropertyValue('--gantt-label-width')
    );
    const labelW = Number.isFinite(labelVar)
        ? Math.max(0, labelVar)
        : viewport.classList.contains('gantt-timeline-pane') ||
            viewport.classList.contains('tl-grid-pane')
          ? 0
          : GANTT_LABEL_WIDTH_PX;
    const leftBound = rect.left + labelW;
    const rightBound = rect.right;
    const edge = GANTT_AUTO_SCROLL.edgePx;

    let intensity = 0;
    let dir = 0;
    if (clientX >= rightBound - edge) {
        intensity = Math.min(1, Math.max(0, (clientX - (rightBound - edge)) / edge));
        dir = 1;
    } else if (clientX <= leftBound + edge) {
        intensity = Math.min(
            1,
            Math.max(0, (leftBound + edge - clientX) / (edge + labelW * 0.35))
        );
        dir = -1;
    }

    // Ease-in curve: gentle near the zone start, faster closer to the rim.
    const curved = intensity * intensity;
    GANTT_AUTO_SCROLL.targetSpeed =
        dir === 0
            ? 0
            : dir *
              (GANTT_AUTO_SCROLL.minSpeed +
                  curved * (GANTT_AUTO_SCROLL.maxSpeed - GANTT_AUTO_SCROLL.minSpeed));

    if (dir !== 0 || Math.abs(GANTT_AUTO_SCROLL.speed) > 0.5) {
        ensureGanttAutoScrollLoop();
    }
}

function onDocumentDragOverAutoScroll(e) {
    updateGanttDragAutoScroll(e.clientX, e.target);
}

export function startGanttDragAutoScroll() {
    if (GANTT_AUTO_SCROLL.listening) return;
    GANTT_AUTO_SCROLL.listening = true;
    GANTT_AUTO_SCROLL.speed = 0;
    GANTT_AUTO_SCROLL.targetSpeed = 0;
    GANTT_AUTO_SCROLL.carry = 0;
    document.addEventListener('dragover', onDocumentDragOverAutoScroll);
}

export function stopGanttDragAutoScroll() {
    GANTT_AUTO_SCROLL.targetSpeed = 0;
    GANTT_AUTO_SCROLL.speed = 0;
    GANTT_AUTO_SCROLL.carry = 0;
    GANTT_AUTO_SCROLL.viewport = null;
    GANTT_AUTO_SCROLL.lastTs = 0;
    if (GANTT_AUTO_SCROLL.rafId != null) {
        cancelAnimationFrame(GANTT_AUTO_SCROLL.rafId);
        GANTT_AUTO_SCROLL.rafId = null;
    }
    if (GANTT_AUTO_SCROLL.listening) {
        document.removeEventListener('dragover', onDocumentDragOverAutoScroll);
        GANTT_AUTO_SCROLL.listening = false;
    }
}

/** Validate a snapped pin drop before commit (keep-and-bump or next-slot fallback). */
export function resolvePinDropCommit(
    task,
    preferred,
    siblingTasks = [],
    { allowOutsideWindow = false } = {}
) {
    const hours = Math.max(
        0.25,
        Number(task.allocated_hours ?? task.estimated_hours) || 1
    );
    const busy = busyIntervalsFromTasks(siblingTasks, task.id);
    const start = moment(preferred);
    const end = start.clone().add(hours, 'hours');
    const deadline = task.deadline_at ? moment(task.deadline_at) : null;

    if (!allowOutsideWindow && deadline?.isValid() && end.isAfter(deadline)) {
        const slot = findNextAvailableSlotClient({
            requestedStart: preferred,
            hours,
            busyIntervals: busy,
            windowStart: task.window_start_at || task.created_date,
            deadline: task.deadline_at,
        });
        if (!slot.ok) {
            return { ok: false, message: slot.message };
        }
        return {
            ok: true,
            preferred: slot.start,
            shifted: true,
            slotMessage: slot.message,
            bumpCount: 0,
        };
    }

    const bumpCount = busy.filter((b) => {
        const bStart = moment(b.start);
        const bEnd = moment(b.end);
        return start.isBefore(bEnd) && end.isAfter(bStart);
    }).length;

    return {
        ok: true,
        preferred,
        shifted: false,
        bumpCount,
        slotMessage:
            bumpCount > 0
                ? `Will move ${bumpCount} conflicting task${bumpCount === 1 ? '' : 's'} after this one`
                : 'Slot available.',
    };
}

/**
 * Team Planner–style HTML5 drag for pin (bar stays put; ghost + drop indicator move).
 * Avoids React fighting inline left styles during drag.
 */
export function createGanttPinDragHandlers({
    dragRef,
    setDropTarget,
    rangeStart,
    totalMs,
    onPinCommit,
    onSlotDenied,
    allowOutsideWindow = true,
}) {
    const onBarDragStart = (e, task, rowUserId) => {
        e.stopPropagation();
        dragRef.current = {
            cardId: Number(task.id),
            fromUserId: Number(rowUserId),
            task,
        };
        e.dataTransfer.effectAllowed = 'move';
        try {
            e.dataTransfer.setData('text/plain', String(task.id));
            if (typeof e.dataTransfer.setDragImage === 'function' && e.currentTarget) {
                const rect = e.currentTarget.getBoundingClientRect();
                e.dataTransfer.setDragImage(
                    e.currentTarget,
                    Math.min(24, rect.width / 2),
                    Math.min(14, rect.height / 2)
                );
            }
        } catch (_) {
            /* some browsers require setData */
        }
        startGanttDragAutoScroll();
        ganttLaneLog('pin-drag-start-html5', {
            cardId: task.id,
            title: task.title,
            userId: rowUserId,
        });
    };

    const onBarDragEnd = () => {
        stopGanttDragAutoScroll();
        dragRef.lastDropAt = Date.now();
        dragRef.current = null;
        setDropTarget(null);
    };

    const onTrackDragOver = (e, rowUserId) => {
        if (!dragRef.current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        updateGanttDragAutoScroll(e.clientX, e.currentTarget);
        setDropTarget({
            userId: Number(rowUserId),
            leftPct: dropLeftPctFromTrackEvent(e.currentTarget, e.clientX),
        });
    };

    const onTrackDrop = async (e, rowUserId, rowTasks) => {
        e.preventDefault();
        stopGanttDragAutoScroll();
        const drag = dragRef.current;
        setDropTarget(null);
        dragRef.current = null;
        if (!drag || Number(drag.fromUserId) !== Number(rowUserId)) return;

        const task =
            drag.task || (rowTasks || []).find((t) => Number(t.id) === drag.cardId);
        if (!task) return;

        const dropMoment = snapToTeamTwoHourSlot(
            timeFromTrackClientX(e.currentTarget, e.clientX, rangeStart, totalMs)
        );
        const preferred = allowOutsideWindow
            ? dropMoment.toISOString()
            : clampPreferredStart(
                  dropMoment,
                  task.window_start_at || task.created_date,
                  task.deadline_at
              );
        const resolved = resolvePinDropCommit(task, preferred, rowTasks, {
            allowOutsideWindow,
        });

        ganttLaneLog('pin-drag-commit-html5', {
            cardId: task.id,
            preferred: resolved.preferred || preferred,
            ok: resolved.ok,
            shifted: resolved.shifted,
            bumpCount: resolved.bumpCount || 0,
        });

        if (!resolved.ok) {
            onSlotDenied?.(resolved.message);
            return;
        }

        try {
            await onPinCommit?.(task, resolved.preferred, rowTasks, {
                shifted: resolved.shifted,
                slotMessage: resolved.slotMessage,
            });
        } catch (err) {
            ganttLaneLog('pin-drag-commit-error', {
                cardId: task.id,
                message: err?.message || String(err),
            });
        }
    };

    return { onBarDragStart, onBarDragEnd, onTrackDragOver, onTrackDrop };
}

/** Skip bar click right after a pin drag (browsers often fire click after dragend). */
export function shouldSuppressGanttBarClick(dragRef) {
    const ts = dragRef?.lastDropAt;
    return ts != null && Date.now() - ts < 400;
}

/** Busy intervals for every task except excludeCardId (scheduled work only). */
export function busyIntervalsFromTasks(tasks = [], excludeCardId = null) {
    return (tasks || [])
        .filter((t) => excludeCardId == null || Number(t.id) !== Number(excludeCardId))
        .map((t) => {
            const start = moment(t.scheduled_start_at || t.planned_start_at);
            const end = moment(
                t.scheduled_end_at ||
                    (start.isValid()
                        ? start
                              .clone()
                              .add(
                                  Math.max(
                                      0.25,
                                      Number(t.allocated_hours ?? t.estimated_hours) || 1
                                  ),
                                  'hours'
                              )
                        : null)
            );
            if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;
            return {
                start: start.toISOString(),
                end: end.toISOString(),
                card_id: t.id,
                title: t.title,
            };
        })
        .filter(Boolean);
}

/**
 * Earliest start at/after requestedStart that does not overlap busyIntervals.
 * Mirrors the backend schedule-engine slot finder (wall-clock hours).
 */
export function findNextAvailableSlotClient({
    requestedStart,
    hours,
    busyIntervals = [],
    windowStart = null,
    deadline = null,
} = {}) {
    const req = moment(requestedStart);
    if (!req.isValid()) {
        return { ok: false, message: 'Invalid start time.' };
    }
    const hrs = Math.max(0.25, Number(hours) || 1);
    const busy = (busyIntervals || [])
        .map((b) => ({
            start: moment(b.start),
            end: moment(b.end),
            card_id: b.card_id,
            title: b.title,
        }))
        .filter((b) => b.start.isValid() && b.end.isValid() && b.end.isAfter(b.start))
        .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    let candidate = req.clone();
    if (windowStart) {
        const ws = moment(windowStart);
        if (ws.isValid() && candidate.isBefore(ws)) candidate = ws.clone();
    }
    const dl = deadline ? moment(deadline) : null;
    const dlValid = dl?.isValid() ? dl : null;

    for (let guard = 0; guard < 200; guard += 1) {
        if (dlValid && candidate.isAfter(dlValid)) {
            return {
                ok: false,
                message: 'No available slot before the due date for this task.',
            };
        }
        const end = candidate.clone().add(hrs, 'hours');
        if (dlValid && end.isAfter(dlValid)) {
            return {
                ok: false,
                message: 'No available slot that fits before the due date.',
            };
        }

        let collisionEnd = null;
        for (const block of busy) {
            if (candidate.isBefore(block.end) && end.isAfter(block.start)) {
                if (!collisionEnd || block.end.isAfter(collisionEnd)) {
                    collisionEnd = block.end.clone();
                }
            }
        }

        if (!collisionEnd) {
            const shifted = candidate.valueOf() !== req.valueOf();
            return {
                ok: true,
                start: candidate.toISOString(),
                end: end.toISOString(),
                shifted,
                message: shifted
                    ? 'That time was busy — moved to the next available slot.'
                    : 'Slot available.',
            };
        }

        candidate = collisionEnd.clone();
    }

    return { ok: false, message: 'No available slot found on this timeline.' };
}

/**
 * Gantt-style bar drag: the grab point stays under the pointer (duration
 * preserved). Live preview is free — no working-hours / window clamp while
 * moving. Snap to 2h slots only on pointerup. Past-due tasks stay where
 * dropped (keep-and-bump), matching the pin-start API.
 */
export function bindTimelinePinDrag({
    event,
    task,
    rangeStart,
    totalMs,
    dragRef,
    setPinPreview,
    onCommit,
    onClick,
    siblingTasks = [],
    onSlotDenied = null,
}) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const barEl = event.currentTarget;
    const track =
        barEl?.closest?.('.user-gantt-track') || barEl?.closest?.('.tl-track');
    const scaleEl = barEl?.closest?.('.tl-grid') || track;
    if (!barEl || !track || !scaleEl || !totalMs) return;

    const pointerId = event.pointerId;
    // Do not setPointerCapture: scrolling the timeline (auto-scroll) while
    // a pointer is captured fires pointercancel in Chrome and snaps the bar back.

    let rafId = null;
    let liveLoop = false;
    let usingMouseFallback = false;
    let indicatorEl = null;
    const hours = Math.max(
        0.25,
        Number(task.allocated_hours ?? task.estimated_hours) || 1
    );
    const busy = busyIntervalsFromTasks(siblingTasks, task.id);
    const rangeStartMs = moment(rangeStart).valueOf();
    const originalStart = moment(
        task.scheduled_start_at || task.planned_start_at || task.preferred_start_at
    );
    const pointerDownTime = timeFromTrackClientX(
        scaleEl,
        event.clientX,
        rangeStart,
        totalMs
    );
    const grabOffsetMs = originalStart.isValid()
        ? pointerDownTime.valueOf() - originalStart.valueOf()
        : 0;
    const originLeftPx = barEl.offsetLeft;
    const scaleWidth = () => Math.max(scaleEl.offsetWidth || track.offsetWidth || 0, 1);

    ganttLaneLog('pin-drag-start', {
        cardId: task.id,
        title: task.title,
        hours,
        busyCount: busy.length,
        busy,
        grabOffsetMs,
        mode: 'keep-drop-bump-others',
    });

    dragRef.current = {
        task,
        track,
        barEl,
        startX: event.clientX,
        lastClientX: event.clientX,
        moved: false,
        preferred: null,
        pointerId,
    };

    document.documentElement.classList.add('gantt-bar-dragging');
    barEl.classList.add('is-pin-dragging');
    barEl.style.transition = 'none';
    barEl.style.willChange = 'transform';

    const leftPctFromMoment = (m) => {
        const ms = moment(m).valueOf();
        if (!Number.isFinite(ms) || !totalMs) return 0;
        return ((ms - rangeStartMs) / totalMs) * 100;
    };

    const startFromClientX = (clientX, { snap = false } = {}) => {
        const pointerTime = timeFromTrackClientX(
            scaleEl,
            clientX,
            rangeStart,
            totalMs
        );
        const rawStart = moment(pointerTime.valueOf() - grabOffsetMs);
        if (!snap) return rawStart;
        return snapToTeamTwoHourSlot(rawStart);
    };

    const resolveAt = (clientX, { snap = false } = {}) => {
        const rawStart = startFromClientX(clientX, { snap: false });
        const preferredMoment = snap ? snapToTeamTwoHourSlot(rawStart) : rawStart;
        const preferred = preferredMoment.toISOString();
        const previewLeftPct = leftPctFromMoment(preferredMoment);

        if (!snap) {
            return {
                taskId: Number(task.id),
                leftPct: previewLeftPct,
                label: formatGanttDateTime(preferred),
                preferred,
                slotError: null,
                shifted: false,
                bumpCount: 0,
            };
        }

        const resolved = resolvePinDropCommit(task, preferred, siblingTasks, {
            allowOutsideWindow: true,
        });
        if (!resolved.ok) {
            return {
                taskId: Number(task.id),
                leftPct: previewLeftPct,
                label: formatGanttDateTime(preferred),
                preferred,
                slotError: resolved.message,
                shifted: false,
                bumpCount: 0,
            };
        }
        return {
            taskId: Number(task.id),
            leftPct: leftPctFromMoment(resolved.preferred),
            label: formatGanttDateTime(resolved.preferred),
            preferred: resolved.preferred,
            slotError: null,
            shifted: resolved.shifted,
            bumpCount: resolved.bumpCount || 0,
            slotMessage: resolved.slotMessage,
        };
    };

    const ensureIndicator = () => {
        if (indicatorEl || !track) return indicatorEl;
        indicatorEl = document.createElement('div');
        indicatorEl.className = 'team-drop-indicator';
        indicatorEl.setAttribute('aria-hidden', 'true');
        track.appendChild(indicatorEl);
        return indicatorEl;
    };

    const clearDragChrome = () => {
        document.documentElement.classList.remove('gantt-bar-dragging');
        stopGanttDragAutoScroll();
    };

    const cleanupDom = (resetVisual) => {
        if (barEl) {
            barEl.classList.remove('is-pin-dragging');
            barEl.style.transition = '';
            barEl.style.willChange = '';
            if (resetVisual) {
                barEl.style.left = '';
                barEl.style.transform = '';
            }
        }
        if (indicatorEl?.parentNode) indicatorEl.parentNode.removeChild(indicatorEl);
        indicatorEl = null;
        clearDragChrome();
    };

    const applyBarTransform = (leftPct) => {
        const trackW = scaleWidth();
        const dx = (leftPct / 100) * trackW - originLeftPx;
        barEl.style.transition = 'none';
        barEl.style.willChange = 'transform';
        barEl.style.transform = `translate3d(${dx}px, 0, 0)`;
        barEl.classList.add('is-pin-dragging');
    };

    const flushPreview = () => {
        rafId = null;
        if (!dragRef.current) return;
        const clientX = dragRef.current.lastClientX;
        updateGanttDragAutoScroll(clientX, track);
        const live = resolveAt(clientX, { snap: false });
        dragRef.current.preferred = live.preferred;
        applyBarTransform(live.leftPct);
        const ind = ensureIndicator();
        if (ind) {
            ind.style.left = `${live.leftPct}%`;
            ind.title = live.label || '';
        }
        if (liveLoop && dragRef.current) {
            rafId = requestAnimationFrame(flushPreview);
        }
    };

    const onMove = (ev) => {
        if (!dragRef.current) return;
        if (
            !usingMouseFallback &&
            ev.pointerId != null &&
            pointerId != null &&
            ev.pointerId !== pointerId
        ) {
            return;
        }
        if (Math.abs(ev.clientX - dragRef.current.startX) > 3) {
            dragRef.current.moved = true;
            liveLoop = true;
        }
        dragRef.current.lastClientX = ev.clientX;
        if (!dragRef.current.moved) return;
        if (rafId == null) rafId = requestAnimationFrame(flushPreview);
    };

    const detachPointer = () => {
        window.removeEventListener('pointermove', onMove, true);
        window.removeEventListener('pointerup', onUp, true);
        window.removeEventListener('pointercancel', onPointerCancel, true);
        window.removeEventListener('mousemove', onMove, true);
        window.removeEventListener('mouseup', onUp, true);
        liveLoop = false;
        if (rafId != null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    };

    const attachMouseFallback = () => {
        if (usingMouseFallback) return;
        usingMouseFallback = true;
        window.removeEventListener('pointermove', onMove, true);
        window.removeEventListener('pointerup', onUp, true);
        window.removeEventListener('pointercancel', onPointerCancel, true);
        window.addEventListener('mousemove', onMove, true);
        window.addEventListener('mouseup', onUp, true);
    };

    const onPointerCancel = (ev) => {
        if (ev.pointerId != null && pointerId != null && ev.pointerId !== pointerId) {
            return;
        }
        // Auto-scroll / browser gesture cancel — keep dragging via mouse.
        if (dragRef.current?.moved) {
            attachMouseFallback();
            return;
        }
        onUp(ev);
    };

    const onUp = async (ev) => {
        if (
            !usingMouseFallback &&
            ev.pointerId != null &&
            pointerId != null &&
            ev.pointerId !== pointerId
        ) {
            return;
        }
        detachPointer();

        const drag = dragRef.current;
        dragRef.current = null;
        setPinPreview?.(null);

        if (ev.type === 'pointercancel' || !drag?.moved) {
            cleanupDom(true);
            if (ev.type !== 'pointercancel') onClick?.(drag?.task);
            return;
        }

        const finalPreview = resolveAt(ev.clientX, { snap: true });
        ganttLaneLog('pin-drag-commit', {
            cardId: drag.task?.id,
            preferred: finalPreview.preferred,
            shifted: finalPreview.shifted,
            bumpCount: finalPreview.bumpCount || 0,
            slotError: finalPreview.slotError || null,
            slotMessage: finalPreview.slotMessage || null,
            leftPct: finalPreview.leftPct,
        });

        if (finalPreview.slotError) {
            cleanupDom(true);
            onSlotDenied?.(finalPreview.slotError, drag.task);
            return;
        }

        if (barEl) {
            barEl.style.left = `${finalPreview.leftPct}%`;
            barEl.style.transform = '';
            barEl.style.willChange = '';
            barEl.classList.remove('is-pin-dragging');
            barEl.classList.add('is-active');
        }
        if (indicatorEl?.parentNode) indicatorEl.parentNode.removeChild(indicatorEl);
        indicatorEl = null;
        clearDragChrome();

        setPinPreview?.({
            taskId: finalPreview.taskId,
            leftPct: finalPreview.leftPct,
            label: finalPreview.label,
        });
        try {
            await onCommit?.(drag.task, finalPreview.preferred, {
                shifted: finalPreview.shifted,
                slotMessage: finalPreview.slotMessage,
            });
        } finally {
            setPinPreview?.(null);
            requestAnimationFrame(() => {
                if (!barEl) return;
                barEl.style.left = '';
                barEl.style.transform = '';
                barEl.style.transition = '';
                barEl.classList.remove('is-active');
            });
        }
    };

    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onPointerCancel, true);
}

/**
 * Snap to the 2-hour grid, then clamp into working hours unless the caller
 * explicitly allows overnight scheduling.
 */
export function snapToTeamTwoHourSlot(value, options = {}) {
    const m = moment(value);
    if (!m.isValid()) return m;
    const snapMinutes = Number(options.snapMinutes) || TIMELINE_WORKING_HOURS.snapMinutes || 120;
    const snapped = m.clone().seconds(0).milliseconds(0);
    const totalMinutes = snapped.hours() * 60 + snapped.minutes();
    const slotMinutes = Math.round(totalMinutes / snapMinutes) * snapMinutes;
    snapped.startOf('day').add(slotMinutes, 'minutes');
    return clampDateToWorkingHours(snapped, options);
}

export function durationHoursBetween(start, end) {
    const s = moment(start);
    const e = moment(end);
    if (!s.isValid() || !e.isValid()) return MIN_TASK_DURATION_HOURS;
    const hours = e.diff(s, 'hours', true);
    if (!Number.isFinite(hours) || hours <= 0) return MIN_TASK_DURATION_HOURS;
    return Math.round(hours * 100) / 100;
}

function sourceTaskHours(sourceTask) {
    if (sourceTask?.is_milestone) return 0;
    const raw = Number(sourceTask?.allocated_hours ?? sourceTask?.estimated_hours);
    if (sourceTask?.estimated_hours != null && Number(sourceTask.estimated_hours) === 0) {
        return 0;
    }
    if (Number.isFinite(raw) && raw === 0) return 0;
    return Math.max(MIN_TASK_DURATION_HOURS, raw || 1);
}

/**
 * Map a gantt-task-react onDateChange (move or edge-resize) onto our pin payload.
 * Uses the dates the library already computed (timeStep); does not restack other bars.
 */
export function resolveGanttBarDateChange(ganttTask, sourceTask) {
    const start =
        ganttTask?.start instanceof Date ? ganttTask.start : new Date(ganttTask?.start);
    let end = ganttTask?.end instanceof Date ? ganttTask.end : new Date(ganttTask?.end);
    const origHours = sourceTaskHours(sourceTask);
    const nextHours = durationHoursBetween(start, end);
    const isMilestone = sourceTask?.is_milestone || ganttTask?.type === 'milestone';
    const displayed = Number(ganttTask?.displayHours);
    const baseline =
        Number.isFinite(displayed) && displayed > 0 ? displayed : origHours;
    const isResize = !isMilestone && Math.abs(nextHours - baseline) > 0.15;

    if (!Number.isFinite(start.getTime())) {
        return { start: new Date(), end: new Date(), hours: origHours, isResize: false };
    }

    if (isResize) {
        const hours = Math.max(MIN_TASK_DURATION_HOURS, nextHours || MIN_TASK_DURATION_HOURS);
        if (!Number.isFinite(end.getTime()) || end.getTime() <= start.getTime()) {
            end = new Date(start.getTime() + hours * 3600 * 1000);
        }
        return { start, end, hours, isResize: true };
    }

    const hours = origHours > 0 ? origHours : MIN_TASK_DURATION_HOURS;
    return {
        start,
        end: new Date(start.getTime() + hours * 3600 * 1000),
        hours: origHours,
        isResize: false,
    };
}

/**
 * Instantly move a pinned task's bar in local state while the server refreshes.
 * Duration is kept from allocated/estimated hours unless `hoursOverride` is set (resize).
 */
export function applyOptimisticPinToTasks(
    tasks = [],
    cardId,
    preferredStartAt,
    userId = null,
    hoursOverride = null,
    bumpOthers = true
) {
    const targetId = Number(cardId);
    const uid = userId != null && userId !== '' ? Number(userId) : null;
    const matchesTarget = (t) =>
        Number(t.id) === targetId &&
        (uid == null || t.gantt_user_id == null || Number(t.gantt_user_id) === uid);
    const sameRow = (t) =>
        uid == null || t.gantt_user_id == null || Number(t.gantt_user_id) === uid;
    const target = (tasks || []).find(matchesTarget);
    if (!target) return tasks;

    const hours = Math.max(
        MIN_TASK_DURATION_HOURS,
        hoursOverride != null && Number.isFinite(Number(hoursOverride))
            ? Number(hoursOverride)
            : Number(target.allocated_hours ?? target.estimated_hours) || 1
    );
    const start = moment(preferredStartAt);
    if (!start.isValid()) return tasks;
    const end = start.clone().add(hours, 'hours');
    const startIso = start.toISOString();
    const endIso = end.toISOString();

    let bumped = 0;
    const next = tasks.map((task) => {
        if (matchesTarget(task)) {
            const prevDue = moment(task.deadline_at || task.due_date);
            const dueIso =
                !prevDue.isValid() || end.isAfter(prevDue) ? endIso : prevDue.toISOString();
            return {
                ...task,
                preferred_start_at: startIso,
                scheduled_start_at: startIso,
                planned_start_at: startIso,
                scheduled_end_at: endIso,
                timeline_end_at: endIso,
                deadline_at: dueIso,
                due_date: dueIso,
                ...(hoursOverride != null
                    ? { allocated_hours: hours, estimated_hours: hours }
                    : {}),
            };
        }

        if (!bumpOthers || !sameRow(task)) return task;

        const otherStart = moment(task.scheduled_start_at || task.planned_start_at);
        const otherHours = Math.max(
            0.25,
            Number(task.allocated_hours ?? task.estimated_hours) || 1
        );
        const otherEnd = moment(
            task.scheduled_end_at ||
                (otherStart.isValid()
                    ? otherStart.clone().add(otherHours, 'hours')
                    : null)
        );
        if (
            !otherStart.isValid() ||
            !otherEnd.isValid() ||
            !(start.isBefore(otherEnd) && end.isAfter(otherStart))
        ) {
            return task;
        }

        // Push conflicting task to start after the pinned task.
        bumped += 1;
        const bumpStart = end.clone();
        const bumpEnd = bumpStart.clone().add(otherHours, 'hours');
        return {
            ...task,
            preferred_start_at: bumpStart.toISOString(),
            scheduled_start_at: bumpStart.toISOString(),
            planned_start_at: bumpStart.toISOString(),
            scheduled_end_at: bumpEnd.toISOString(),
            timeline_end_at: bumpEnd.toISOString(),
        };
    });

    ganttLaneLog('optimistic-pin', {
        cardId: targetId,
        requested: preferredStartAt,
        applied: startIso,
        end: endIso,
        hours,
        bumped,
        mode: bumpOthers ? 'keep-drop-bump-others' : 'pin-only',
    });

    return next;
}

/** Pin a task on one member's row inside Team Planner group state. */
export function applyOptimisticPinToTeamGroups(
    groups = [],
    userId,
    cardId,
    preferredStartAt,
    hoursOverride = null,
    bumpOthers = true
) {
    return groups.map((g) => ({
        ...g,
        members: (g.members || []).map((m) => {
            if (Number(m.user?.user_id) !== Number(userId)) return m;
            return {
                ...m,
                tasks: applyOptimisticPinToTasks(
                    m.tasks || [],
                    cardId,
                    preferredStartAt,
                    userId,
                    hoursOverride,
                    bumpOthers
                ),
            };
        }),
    }));
}

/**
 * Clamp a preferred start into the planned window [windowStart, deadline].
 * Returns an ISO string or null.
 */
export function clampPreferredStart(dropTime, windowStart, deadline) {
    let start = moment(dropTime);
    if (!start.isValid()) return null;

    const ws = windowStart ? moment(windowStart) : null;
    if (ws?.isValid() && start.isBefore(ws)) start = ws.clone();

    const dl = deadline ? moment(deadline) : null;
    if (dl?.isValid() && start.isAfter(dl)) start = dl.clone();

    return start.toISOString();
}

/** Planned free-move window for a task (created → due). */
export function getTaskPlannedWindow(task) {
    const startRaw = task?.window_start_at || task?.created_date || task?.createdAt;
    const endRaw = task?.deadline_at || null;
    const start = startRaw ? moment(startRaw) : null;
    const end = endRaw ? moment(endRaw) : null;
    return {
        start: start?.isValid() ? start : null,
        end: end?.isValid() ? end : null,
    };
}

/**
 * CSS left/width for a faint planned-window background on the track.
 */
export function plannedWindowStyle(task, rangeStart, totalMs) {
    const { start, end } = getTaskPlannedWindow(task);
    if (!start || !totalMs) return null;
    const rangeStartM = moment(rangeStart);
    const leftPct = Math.max(0, (start.diff(rangeStartM) / totalMs) * 100);
    const endM = end && end.isAfter(start) ? end : start.clone().add(4, 'hours');
    const widthPct = Math.max(0.5, (endM.diff(start) / totalMs) * 100);
    return {
        left: `${leftPct}%`,
        width: `${Math.min(widthPct, 100 - leftPct)}%`,
    };
}

/** Minimum timeline track width so bars are readable and the chart can scroll horizontally.
 *  @param {number} [zoom=1] — multiply density (<1 zooms out so weeks fit; >1 zooms in).
 */
export function computeTimelineWidthPx(rangeStart, rangeEnd, zoom = 1) {
    const start = moment(rangeStart);
    const end = moment(rangeEnd);
    if (!start.isValid() || !end.isValid()) return 720;

    const hours = Math.max(end.diff(start, 'hours', true), 6);
    // More pixels per hour so short work windows are readable and bars don't look like slivers
    const pxPerHour = hours <= 12 ? 120 : hours <= 24 ? 90 : hours <= 72 ? 60 : hours <= 168 ? 40 : 24;
    const z = Math.max(0.15, Number(zoom) || 1);
    // When zoomed out, allow narrower than 960 so a multi-week range can fit the viewport.
    const floor = z < 1 ? Math.max(480, Math.round(960 * z)) : 960;
    return Math.max(floor, Math.ceil(hours * pxPerHour * z));
}

/** Team Planner–style width: one readable column per 2-hour slot. */
export const TEAM_PX_PER_2H_SLOT = 56;
export const TEAM_TICK_STEP = { unit: 'hours', amount: 2 };

export function computeTeamTimelineWidthPx(rangeStart, rangeEnd, zoom = 1) {
    const start = moment(rangeStart);
    const end = moment(rangeEnd);
    if (!start.isValid() || !end.isValid()) return 960;
    const hours = Math.max(end.diff(start, 'hours', true), 6);
    const z = Math.max(0.15, Number(zoom) || 1);
    const floor = z < 1 ? Math.max(480, Math.round(960 * z)) : 960;
    return Math.max(floor, Math.ceil((hours / 2) * TEAM_PX_PER_2H_SLOT * z));
}

export function formatWorkDurationHours(hours) {
    const WORK_DAY_HOURS = 8;
    const total = Math.round((Number(hours) || 0) * 10) / 10;
    if (total <= 0) return '0h';
    const days = Math.floor(total / WORK_DAY_HOURS);
    const rem = Math.round((total - days * WORK_DAY_HOURS) * 10) / 10;
    if (days > 0 && rem > 0) return `${days}d ${rem}h`;
    if (days > 0) return `${days}d`;
    return `${rem}h`;
}

/**
 * Scheduled work interval for conflict checks.
 * Use scheduled_start/end only — NOT timeline_end (spill) or deadline, which
 * inflate the window and falsely flag back-to-back cascade tasks as overlapping.
 */
export function getTaskWorkWindow(task) {
    const startRaw = task?.scheduled_start_at || task?.planned_start_at;
    const endRaw = task?.scheduled_end_at;
    if (!startRaw || !endRaw) return null;

    const start = moment(startRaw);
    const end = moment(endRaw);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;

    return { start, end };
}

/** Map task id → list of other tasks whose scheduled work windows overlap in time. */
export function detectTaskTimeOverlaps(tasks = []) {
    const overlapMap = new Map();
    const entries = tasks
        .map((task) => ({ task, window: getTaskWorkWindow(task) }))
        .filter((entry) => entry.window);

    const addPair = (taskA, taskB, overlapMs) => {
        if (!overlapMap.has(taskA.id)) overlapMap.set(taskA.id, []);
        const list = overlapMap.get(taskA.id);
        if (!list.some((item) => item.id === taskB.id)) {
            list.push({
                id: taskB.id,
                title: taskB.title,
                overlap_minutes: Math.round(overlapMs / 60000),
            });
        }
    };

    for (let i = 0; i < entries.length; i += 1) {
        for (let j = i + 1; j < entries.length; j += 1) {
            const a = entries[i].window;
            const b = entries[j].window;
            // Touching end-to-start (A ends exactly when B starts) is NOT an overlap.
            const overlapStart = moment.max(a.start, b.start);
            const overlapEnd = moment.min(a.end, b.end);
            const overlapMs = overlapEnd.diff(overlapStart);
            if (overlapMs > 1000) {
                addPair(entries[i].task, entries[j].task, overlapMs);
                addPair(entries[j].task, entries[i].task, overlapMs);
            }
        }
    }

    return overlapMap;
}

export function formatOverlapList(overlaps = []) {
    if (!overlaps.length) return null;
    const labels = overlaps.map((item) => item.title || `#${item.id}`);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
}

/**
 * Timeline bar segments: scheduled work (solid), due marker, spill (red stripes), optional preview.
 */
export function buildGanttWorkBarSegments(task, rangeStart, totalMs, options = {}) {
    const rangeStartM = moment(rangeStart);
    const workStart = task.scheduled_start_at
        ? moment(task.scheduled_start_at)
        : task.planned_start_at
          ? moment(task.planned_start_at)
          : rangeStartM.clone();
    const workEnd = task.scheduled_end_at
        ? moment(task.scheduled_end_at)
        : workStart.clone().add(1, 'hour');
    const dueAt = task.deadline_at ? moment(task.deadline_at) : null;
    const proposedDue = options.proposedDue ? moment(options.proposedDue) : null;
    const timelineEnd = task.timeline_end_at ? moment(task.timeline_end_at) : workEnd;
    const spillHours = Number(task.bandwidth_spill_hours) || 0;
    const hasSpill =
        spillHours > 0 || Boolean(task.missed_deadline && dueAt && workEnd.isAfter(dueAt));

    const leftPct = Math.max(0, (workStart.diff(rangeStartM) / totalMs) * 100);
    // Keep true duration; only enforce a tiny floor so 0-width bars stay clickable.
    const workWidth = Math.max(0.35, (workEnd.diff(workStart) / totalMs) * 100);
    const cappedWorkWidth = Math.min(workWidth, 100 - leftPct);

    let spill = null;
    if (!proposedDue && hasSpill && dueAt && workEnd.isAfter(dueAt)) {
        const dueLeft = (dueAt.diff(rangeStartM) / totalMs) * 100;
        const spillEnd = timelineEnd.isAfter(workEnd) ? timelineEnd : workEnd;

        if (dueLeft < leftPct) {
            spill = {
                left: `${leftPct}%`,
                width: `${cappedWorkWidth}%`,
            };
        } else {
            const spillLeft = Math.max(leftPct, dueLeft);
            const spillWidth = Math.max(1, (spillEnd.diff(dueAt) / totalMs) * 100);
            const cappedSpillWidth = Math.min(spillWidth, 100 - spillLeft);
            if (cappedSpillWidth > 0.5) {
                spill = {
                    left: `${spillLeft}%`,
                    width: `${cappedSpillWidth}%`,
                };
            }
        }
    }

    let preview = null;
    if (proposedDue?.isValid()) {
        let previewStart = null;
        if (dueAt && proposedDue.isAfter(dueAt)) {
            previewStart = dueAt;
        } else if (proposedDue.isAfter(workEnd)) {
            previewStart = workEnd;
        }
        if (previewStart) {
            const effectiveStart = moment.max(previewStart, rangeStartM);
            const previewLeft = Math.max(0, (effectiveStart.diff(rangeStartM) / totalMs) * 100);
            const previewWidth = Math.max(2, (proposedDue.diff(effectiveStart) / totalMs) * 100);
            if (previewWidth > 0.5) {
                preview = {
                    left: `${previewLeft}%`,
                    width: `${Math.min(previewWidth, 100 - previewLeft)}%`,
                };
            }
        }
    }

    let dueMarker = null;
    if (dueAt?.isValid()) {
        const dueLeft = (dueAt.diff(rangeStartM) / totalMs) * 100;
        if (dueLeft >= 0 && dueLeft <= 100) {
            dueMarker = { left: `${dueLeft}%`, isProposed: Boolean(proposedDue) };
        }
    }

    return {
        work: {
            left: `${leftPct}%`,
            width: `${cappedWorkWidth}%`,
        },
        spill,
        preview,
        dueMarker,
        dueBeforeWork: dueAt ? dueAt.isBefore(workStart) : false,
        isNarrow: options.timelineWidthPx
            ? (cappedWorkWidth / 100) * Number(options.timelineWidthPx) < 72
            : cappedWorkWidth < 8,
    };
}

export const TIMELINE_BAR_STATUS = {
    scheduled: { id: 'scheduled', label: 'Scheduled', color: '#3b82f6' },
    done: { id: 'done', label: 'Done', color: '#22c55e' },
    dueSoon: { id: 'dueSoon', label: 'Due soon', color: '#f59f00' },
    pastDue: { id: 'pastDue', label: 'Past due', color: '#ef4444' },
};

export function timelineBarStatus(task = {}, now = new Date()) {
    const progress = Number(task.progress) || 0;
    const listName = String(task.list_name || task.list?.name || '').toLowerCase();
    if (progress >= 100 || listName === 'done' || listName === 'completed') {
        return TIMELINE_BAR_STATUS.done;
    }
    const due = task.deadline_at ? moment(task.deadline_at) : null;
    const workEnd = task.scheduled_end_at ? moment(task.scheduled_end_at) : null;
    const nowM = moment(now);
    if (
        task.missed_deadline ||
        (due?.isValid() && due.isBefore(nowM)) ||
        (due?.isValid() && workEnd?.isValid() && workEnd.isAfter(due))
    ) {
        return TIMELINE_BAR_STATUS.pastDue;
    }
    if (due?.isValid() && due.diff(nowM, 'hours', true) <= 48) {
        return TIMELINE_BAR_STATUS.dueSoon;
    }
    return TIMELINE_BAR_STATUS.scheduled;
}

export function priorityTone(priority) {
    const v = String(priority || '').toLowerCase();
    if (v === 'urgent' || v === 'high') return 'high';
    if (v === 'low') return 'low';
    return 'normal';
}

/** Pack overlapping work windows into stacked lanes (touching end-to-start share a lane). */
export function assignOverlapLanes(tasks = []) {
    const items = (tasks || []).map((task, index) => {
        const window = getTaskWorkWindow(task);
        return {
            task,
            index,
            start: window?.start?.valueOf() ?? Number.MAX_SAFE_INTEGER,
            end: window?.end?.valueOf() ?? 0,
        };
    });
    items.sort((a, b) => a.start - b.start || a.index - b.index);
    const laneEnds = [];
    const laneByKey = new Map();
    items.forEach((item) => {
        let lane = 0;
        while (lane < laneEnds.length && item.start < laneEnds[lane] - 1000) {
            lane += 1;
        }
        if (lane === laneEnds.length) laneEnds.push(item.end);
        else laneEnds[lane] = Math.max(laneEnds[lane], item.end);
        const key = item.task.gantt_row_key || `${item.task.id}:${item.task.gantt_user_id || ''}`;
        laneByKey.set(key, lane);
        laneByKey.set(String(item.task.id), lane);
    });
    return { laneByKey, laneCount: Math.max(1, laneEnds.length) };
}

/** Distinct overlap intervals for hatch overlays (never merge task identity). */
export function buildOverlapHatchSegments(tasks = [], rangeStart, totalMs) {
    const range = moment(rangeStart);
    const windows = (tasks || [])
        .map((task) => ({ task, window: getTaskWorkWindow(task) }))
        .filter((entry) => entry.window);
    const segs = [];
    for (let i = 0; i < windows.length; i += 1) {
        for (let j = i + 1; j < windows.length; j += 1) {
            const a = windows[i].window;
            const b = windows[j].window;
            const overlapStart = moment.max(a.start, b.start);
            const overlapEnd = moment.min(a.end, b.end);
            if (overlapEnd.diff(overlapStart) <= 1000 || !totalMs) continue;
            const left = Math.max(0, (overlapStart.diff(range) / totalMs) * 100);
            const width = Math.max(0.4, (overlapEnd.diff(overlapStart) / totalMs) * 100);
            segs.push({
                key: `${windows[i].task.id}-${windows[j].task.id}`,
                left: `${left}%`,
                width: `${Math.min(width, 100 - left)}%`,
                aId: windows[i].task.id,
                bId: windows[j].task.id,
            });
        }
    }
    return segs;
}

export function buildConflictBanner(personName, tasks = [], now = new Date()) {
    const list = tasks || [];
    if (!list.length) return null;
    const totalHours = list.reduce(
        (sum, t) => sum + (Number(t.allocated_hours ?? t.estimated_hours) || 0),
        0
    );
    const deadlines = list
        .map((t) => (t.deadline_at ? moment(t.deadline_at) : null))
        .filter((m) => m?.isValid());
    const lastDeadline = deadlines.length ? moment.max(deadlines) : null;
    const available = lastDeadline
        ? workingHoursBetweenWindow(now, lastDeadline)
        : 0;
    const spillHours = list.reduce(
        (sum, t) => sum + (Number(t.bandwidth_spill_hours) || 0),
        0
    );
    const pastDueCount = list.filter((t) => {
        if (t.missed_deadline) return true;
        const due = t.deadline_at ? moment(t.deadline_at) : null;
        return due?.isValid() && due.isBefore(now);
    }).length;
    const overlapMap = detectTaskTimeOverlaps(list);
    const deficit = Math.max(0, Math.round((totalHours - available) * 10) / 10);
    const hasConflict =
        deficit > 0.05 || pastDueCount > 0 || spillHours > 0.05 || overlapMap.size > 0;
    if (!hasConflict) return null;

    const workLabel = formatWorkDurationHours(totalHours);
    const availLabel = formatWorkDurationHours(available);
    const name = personName || 'This person';
    const parts = [
        `${name} has ${workLabel} of work and ${availLabel} of working time before the deadlines.`,
    ];
    if (deficit > 0) {
        parts.push(`${Math.round(deficit)}h will run past due unless something moves.`);
    }
    if (pastDueCount > 0) {
        parts.push(
            `${pastDueCount} task${pastDueCount === 1 ? ' is' : 's are'} already past due.`
        );
    }
    return {
        text: parts.join(' '),
        deficitHours: deficit,
        pastDueCount,
        overlapCount: overlapMap.size,
        spillHours,
        overlapMap,
    };
}
